import { Box } from '@mui/material';
import {
  CustomBarChart,
  CustomDonutChart,
  ChartFooterChard,
  Typography,
  colors,
  IconGraphy,
  Avatar,
  styles,
  Dropdown,
} from '@leapeasy/ui-kit';
import { useState, useMemo, useEffect } from 'react';
import { buildDonutPercentData } from 'utils/claimChartDonutPercents';
import { buildChartYearList, resolveSelectedChartYear, toChartYear } from 'utils/claimChartYears';
import { formatDashboardCurrency } from 'utils/formatDashboardCount';

export const ClaimPaymentsChart = ({
  data,
  onDrilldownClosedBucket,
  onDrilldownPendingPayment,
  onDrilldownOther,
}) => {
  const toNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const [year, setYear] = useState(new Date().getFullYear());

  const monthlyAggregates = (
    Array.isArray(data?.claimPaymentsByMonth) ? data.claimPaymentsByMonth : []
  ).filter((aggregate) => aggregate && typeof aggregate === 'object');

  let closedClaims = 0;
  let openPendingPayment = 0;
  let other = 0;

  for (const aggregates of monthlyAggregates) {
    closedClaims +=
      toNumber(aggregates.closed_partial_denial) +
      toNumber(aggregates.closed_duplicate) +
      toNumber(aggregates.closed_paid) +
      toNumber(aggregates.closed_denied) +
      toNumber(aggregates.closed_inactive) +
      toNumber(aggregates.closed_incident_only);

    openPendingPayment += toNumber(aggregates.open_pending_payment);
    other +=
      toNumber(aggregates.incident_only) +
      toNumber(aggregates.open_test_claim) +
      toNumber(aggregates.open_pending_decision);
  }

  const totalIndemnity = closedClaims + openPendingPayment + other;

  const indemnityDonutData = useMemo(
    () =>
      buildDonutPercentData([
        {
          name: 'Closed - Claim Paid',
          value: closedClaims,
        },
        {
          name: 'Open - Pending Payment',
          value: openPendingPayment,
        },
        {
          name: 'Other',
          value: other,
        },
      ]),
    [closedClaims, openPendingPayment, other],
  );

  const yearList = useMemo(() => buildChartYearList(monthlyAggregates), [monthlyAggregates]);

  useEffect(() => {
    setYear((currentYear) => resolveSelectedChartYear(yearList, currentYear));
  }, [yearList]);

  const selectedYear = resolveSelectedChartYear(yearList, year);
  const monthlyChartData = monthlyAggregates
    .filter((aggregate) => toChartYear(aggregate?.year) === selectedYear)
    .map((aggregate) => ({
      ...aggregate,
      name: aggregate.month,
      total: toNumber(aggregate.total),
    }));

  return (
    <Box
      sx={{
        boxShadow: styles.boxShadow.sm,
        borderRadius: `${styles.borderRadius.large}px`,
        border: `1px solid ${colors.black[300]}`,
        width: '100%',
        padding: '16px',
        boxSizing: 'border-box',
      }}
      display="flex"
      flexDirection="column"
      gap="12px"
    >
      <Box display="flex" gap="16px">
        <Avatar size="large" iconImage={<IconGraphy icon="FileFolder.Contract" />} />
        <Box>
          <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Claims Payments Issued
          </Typography>

          <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Here is an overview of the claims payments issued, displaying monthly values and the
            total indemnity payments categorized by status.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: '#f9f8f9',
          border: `1px solid ${colors.purple[100]}`,
          borderRadius: '8px',
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
        display="flex"
        gap="20px"
      >
        <Box
          sx={{
            flex: '1.8 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box display="flex" gap="4px" flexDirection="column">
              <Typography variant="body1" style={{ fontWeight: '600', color: colors.purple[900] }}>
                Claims value month over month
              </Typography>
              <Typography variant="body3" style={{ color: colors.neutral[900] }}>
                Please choose the year for which you would like to view monthly values.
              </Typography>
            </Box>

            <Dropdown
              value={selectedYear}
              size="medium"
              onChange={(e) => setYear(toChartYear(e.target.value) ?? selectedYear)}
              options={yearList.map((yearOption) => ({
                text: yearOption.toString(),
                value: yearOption,
              }))}
              multiselect={false}
              placeholder="Select item"
              showSearch={false}
              icon={<IconGraphy icon="TimeDate.CalendarMonth" />}
              iconVisible={true}
              label="Year"
            />
          </Box>

          <CustomBarChart
            chartWidth="100%"
            chartHeight={200}
            data={monthlyChartData}
            colors={{
              total: colors.warmBlue[100],
            }}
          />
        </Box>

        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap="20px"
        >
          <Typography variant="body1" style={{ fontWeight: '600' }}>
            Total Indemnity Payments
          </Typography>

          <CustomDonutChart
            chartWidth={200}
            chartHeight={200}
            donutMinAngle={30}
            data={indemnityDonutData}
            isPercentageValue={true}
            colors={[colors.tomato[100], colors.parisGreen[100], colors.black[500]]}
            title="Total Claim"
            description={formatDashboardCurrency(totalIndemnity)}
          />

          <Box display="flex" flexDirection="column" gap="8px" width="100%">
            <ChartFooterChard
              backgroundColor={colors.tomato[25]}
              activeColor={colors.tomato[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {formatDashboardCurrency(closedClaims)}
                </Typography>
              }
              content={
                <Typography
                  variant="small"
                  style={{ fontWeight: '500', color: colors.neutral[900] }}
                >
                  Closed — claim paid bucket
                </Typography>
              }
              onClick={() => onDrilldownClosedBucket?.()}
            />
            <ChartFooterChard
              backgroundColor={colors.parisGreen[25]}
              activeColor={colors.parisGreen[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {formatDashboardCurrency(openPendingPayment)}
                </Typography>
              }
              content={
                <Typography
                  variant="small"
                  style={{ fontWeight: '500', color: colors.neutral[900] }}
                >
                  Open — pending payment
                </Typography>
              }
              onClick={() => onDrilldownPendingPayment?.()}
            />
            <ChartFooterChard
              backgroundColor={colors.black[100]}
              activeColor={colors.black[500]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {formatDashboardCurrency(other)}
                </Typography>
              }
              content={
                <Typography
                  variant="small"
                  style={{ fontWeight: '500', color: colors.neutral[900] }}
                >
                  Other open statuses
                </Typography>
              }
              onClick={() => onDrilldownOther?.()}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
